import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import NewsListCard from "@/components/feature/NewsListCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/lib/seo";
import { getHotMovies, getLatestNews, searchMovies, searchNews } from "@/lib/media";

type ResultTab = "media" | "news";

const hotKeywords = ["悬疑", "治愈", "雾港迷踪", "综艺", "2025", "采访"];

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [inputValue, setInputValue] = useState(keyword);
  const [tab, setTab] = useState<ResultTab>("media");

  useEffect(() => {
    setInputValue(keyword);
    setTab("media");
  }, [keyword]);

  usePageMeta({
    title: keyword ? `"${keyword}"的搜索结果 - 枫与铃` : "搜索影视与资讯 - 枫与铃",
    description: keyword
      ? `在枫与铃站内搜索“${keyword}”，查看相关的影视作品与影视资讯内容。`
      : "搜索枫与铃站内的电影、电视剧、综艺作品与影视资讯文章。",
    keywords: keyword ? `${keyword},影视搜索,枫与铃` : "影视搜索,枫与铃",
  });

  const movieResults = useMemo(() => searchMovies(keyword), [keyword]);
  const newsResults = useMemo(() => searchNews(keyword), [keyword]);
  const hotMovies = getHotMovies(4);
  const latestNews = getLatestNews(4);

  const submit = (value: string) => {
    const next = value.trim();
    if (!next) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: next });
  };

  const total = movieResults.length + newsResults.length;
  const hasKeyword = keyword.trim().length > 0;

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-[72px]">
        <section className="border-b border-background-200 bg-background-100/80">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="cursor-pointer hover:text-primary-600">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm" />
              </span>
              <span className="text-foreground-800">搜索</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              搜索
            </h1>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit(inputValue);
              }}
              className="mt-5 flex flex-col gap-3 sm:flex-row"
            >
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 focus-within:border-primary-400">
                <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  type="search"
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder="输入影视名称、导演、类型或资讯关键词"
                  aria-label="搜索关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                {inputValue ? (
                  <button
                    type="button"
                    onClick={() => setInputValue("")}
                    aria-label="清空关键词"
                    className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                  >
                    <i className="ri-close-circle-fill text-sm" />
                  </button>
                ) : null}
              </div>
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-8 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span>热门搜索</span>
              {hotKeywords.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => submit(item)}
                  className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          {hasKeyword ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-foreground-600">
                  关键词 <strong className="text-foreground-950">“{keyword}”</strong> 共找到{" "}
                  <strong className="font-heading text-primary-600">{total}</strong> 条结果
                </p>
                <div className="flex items-center gap-1 rounded-md bg-background-100 p-1">
                  <button
                    type="button"
                    onClick={() => setTab("media")}
                    className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded px-3 py-1.5 text-xs transition-colors ${
                      tab === "media"
                        ? "bg-background-50 font-medium text-primary-600"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    影视作品
                    <span className="text-[10px] text-foreground-500">{movieResults.length}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab("news")}
                    className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded px-3 py-1.5 text-xs transition-colors ${
                      tab === "news"
                        ? "bg-background-50 font-medium text-primary-600"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    资讯文章
                    <span className="text-[10px] text-foreground-500">{newsResults.length}</span>
                  </button>
                </div>
              </div>

              <div className="mt-6">
                {tab === "media" ? (
                  movieResults.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
                      {movieResults.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} />
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon="ri-film-line"
                      title="没有匹配的影视作品"
                      description="试试更短的关键词，或者切换到其他类型浏览。"
                      actionLabel="浏览电影分类"
                      onAction={() => navigate("/category/movie")}
                    />
                  )
                ) : newsResults.length > 0 ? (
                  <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
                    {newsResults.map((item) => (
                      <NewsListCard key={item.id} item={item} />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon="ri-newspaper-line"
                    title="没有匹配的资讯"
                    description="换个关键词试试，比如「预告」或「采访」。"
                    actionLabel="浏览全部资讯"
                    onAction={() => navigate("/news")}
                  />
                )}
              </div>

              {total === 0 ? (
                <div className="mt-12">
                  <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">
                    也可以看看这些
                  </h2>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
                    {hotMovies.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="flex flex-col gap-12">
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-12 text-center">
                <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <h2 className="font-heading text-lg font-semibold text-foreground-950">
                  输入关键词开始搜索
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                  搜索支持影视片名、导演、演员、类型，以及资讯标题与摘要内容。
                </p>
              </div>

              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-heading text-lg font-semibold text-foreground-950">
                    热门影视
                  </h2>
                  <Link
                    to="/category/movie"
                    className="flex cursor-pointer items-center gap-1 text-sm text-foreground-600 hover:text-primary-600"
                  >
                    全部电影
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-s-line text-base" />
                    </span>
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
                  {hotMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-heading text-lg font-semibold text-foreground-950">
                    最新资讯
                  </h2>
                  <Link
                    to="/news"
                    className="flex cursor-pointer items-center gap-1 text-sm text-foreground-600 hover:text-primary-600"
                  >
                    全部资讯
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-s-line text-base" />
                    </span>
                  </Link>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
                  {latestNews.map((item) => (
                    <NewsListCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}